import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { MotionSequence } from '@/components/VRMViewerV2'
import { type HubAvatarIssue, useAvatarSync } from '@/features/avatar-settings/hooks/useAvatarSync'
import { VRMViewerV2 } from '@/features/vrm-viewer/VRMViewerV2'
import { AuthAccessProvider } from './AuthAccessProvider'
import { AvatarLoadNotice } from './AvatarLoadNotice'

export const ViewerConfigContext = createContext<{
  avatarMotions: MotionSequence | null
  setAvatarMotions: (next: MotionSequence | null) => void
  hubIssue: HubAvatarIssue | null
  dismissHubIssue: () => void
} | null>(null)

type VRMViewerProviderProps = {
  children: React.ReactNode
  enabled?: boolean
  avatarFileUrlOverride?: string
}

type SyncedVRMViewerProps = {
  avatarMotions: MotionSequence | null
  avatarFileUrlOverride?: string
  enabled: boolean
  onHubIssue: (state: { hubIssue: HubAvatarIssue | null; dismissHubIssue: () => void }) => void
}

const SyncedVRMViewer = ({ avatarMotions, avatarFileUrlOverride, enabled, onHubIssue }: SyncedVRMViewerProps) => {
  const { avatarFileUrl, hubIssue, dismissHubIssue, noticeMessage, dismissNotice, errorMessage, onAvatarLoadResult } =
    useAvatarSync()
  const resolvedAvatarFileUrl = avatarFileUrlOverride ?? avatarFileUrl

  useEffect(() => {
    onHubIssue({ hubIssue: hubIssue ?? null, dismissHubIssue })
  }, [hubIssue, dismissHubIssue, onHubIssue])

  return (
    <>
      {enabled && avatarMotions && resolvedAvatarFileUrl && (
        <VRMViewerV2
          motionSequence={avatarMotions}
          avatarFile={resolvedAvatarFileUrl}
          onAvatarLoadResult={avatarFileUrlOverride ? undefined : onAvatarLoadResult}
        />
      )}
      {enabled && (noticeMessage || errorMessage) && (
        <AvatarLoadNotice message={noticeMessage || errorMessage || ''} onClose={dismissNotice} />
      )}
    </>
  )
}

export const VRMViewerProvider = ({ children, enabled = true, avatarFileUrlOverride }: VRMViewerProviderProps) => {
  const [avatarMotions, setAvatarMotionsState] = useState<MotionSequence | null>(null)
  const [hasStartedSync, setHasStartedSync] = useState(enabled)
  const [hubState, setHubState] = useState<{ hubIssue: HubAvatarIssue | null; dismissHubIssue: () => void }>({
    hubIssue: null,
    dismissHubIssue: () => undefined,
  })
  useEffect(() => {
    if (enabled) setHasStartedSync(true)
  }, [enabled])
  const setAvatarMotions = useCallback((next: MotionSequence | null) => {
    setAvatarMotionsState(next)
  }, [])

  const contextValue = useMemo(
    () => ({
      avatarMotions,
      setAvatarMotions,
      ...hubState,
    }),
    [avatarMotions, setAvatarMotions, hubState]
  )

  return (
    <ViewerConfigContext.Provider value={contextValue}>
      {(enabled || hasStartedSync) && (
        <SyncedVRMViewer
          avatarMotions={avatarMotions}
          avatarFileUrlOverride={avatarFileUrlOverride}
          enabled={enabled}
          onHubIssue={setHubState}
        />
      )}
      <AuthAccessProvider>{children}</AuthAccessProvider>
    </ViewerConfigContext.Provider>
  )
}

export const useVRMViewerV2 = (nextConfig?: MotionSequence) => {
  const ctx = useContext(ViewerConfigContext)
  if (!ctx) throw new Error('useVRMViewerV2 は VRMViewerProvider の内側で使ってください')

  const { setAvatarMotions } = ctx

  useEffect(() => {
    if (nextConfig) setAvatarMotions(nextConfig)
    else setAvatarMotions(null)
  }, [setAvatarMotions, nextConfig])

  return { setAvatarMotions, hubIssue: ctx.hubIssue, dismissHubIssue: ctx.dismissHubIssue }
}
